import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

export default function BorseArticle() {
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Itaobuy Spreadsheet Borse: Zaini, Tracolla e Tote Bag con Foto QC',
    description: 'Scopri le migliori borse su Itaobuy Spreadsheet: zaini, borse a tracolla, tote bag e modelli di tendenza. Guida italiana con foto QC, prezzi e link diretti.',
    author: { '@type': 'Organization', name: 'Itaobuy Spread' },
    datePublished: '2026-07-14',
    inLanguage: 'it',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: 'Che tipo di borse posso trovare su Itaobuy Spreadsheet?', acceptedAnswer: { '@type': 'Answer', text: 'Zaini urbani, borse a tracolla in pelle, tote bag in tela, borse a mano eleganti e marsupi di tendenza. Ogni borsa include foto QC dei dettagli.' } },
      { '@type': 'Question', name: 'Come valutare una borsa dalle foto QC?', acceptedAnswer: { '@type': 'Answer', text: 'Controlla la qualità della pelle o del tessuto, le cuciture su manici e fondo, le cerniere e le finiture metalliche. Le foto QC mostrano anche l\'interno e gli scomparti.' } },
      { '@type': 'Question', name: 'Itaobuy Spreadsheet ha borse di marche conosciute?', acceptedAnswer: { '@type': 'Answer', text: 'Lo spreadsheet include prodotti di vari produttori. Le foto QC ti aiutano a confrontare qualità e design indipendentemente dal brand.' } },
    ],
  };

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://itaobuyspread.mom';

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
      { '@type': 'ListItem', position: 2, name: 'Itaobuy Spreadsheet Borse', item: `${siteUrl}/itaobuy-spreadsheet-borse` },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <Navbar />
      <main className="pt-20">
        <article className="px-6 md:px-10 max-w-4xl mx-auto py-10 md:py-16">
          <nav className="text-xs text-warm-400 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-tealwarm-600 transition-colors">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-warm-600">Itaobuy Spreadsheet Borse</span>
          </nav>

          <header>
            <span className="inline-block px-3 py-1 rounded-full bg-tealwarm-100 text-tealwarm-700 text-xs font-semibold uppercase tracking-wide mb-4">
              Categoria Borse
            </span>
            <h1 className="font-display text-3xl md:text-5xl font-bold text-warm-950 leading-tight">
              <span className="text-tealwarm-600">Itaobuy Spreadsheet Borse:</span> Zaini, Tracolla e Tote
            </h1>
            <p className="mt-4 text-sm md:text-base text-warm-600 leading-relaxed">
              La categoria <strong>itaobuy spreadsheet borse</strong> è un tesoro per chi cerca zaini funzionali, eleganti borse a tracolla o pratiche tote bag. Ogni articolo viene presentato con foto QC che mostrano l&apos;esterno, l&apos;interno, le cerniere e ogni dettaglio che conta quando si sceglie una borsa di qualità. Non serve più immaginare: con lo spreadsheet vedi tutto.
            </p>
          </header>

          <div className="mt-10">
            <img
              src="https://readdy.ai/api/search-image?query=Three%20stylish%20bags%20a%20cognac%20leather%20backpack%20a%20cream%20canvas%20tote%20and%20a%20black%20crossbody%20bag%20arranged%20on%20a%20wooden%20bench%20in%20a%20sunlit%20corner%20of%20a%20room%20with%20a%20fiddle%20leaf%20fig%20plant%20nearby%2C%20warm%20natural%20light%20creating%20soft%20shadows%2C%20lifestyle%20product%20photography%20with%20cozy%20urban%20apartment%20aesthetic&width=1200&height=600&seq=article-borse-hero&orientation=landscape"
              alt="Itaobuy Spreadsheet Borse zaini"
              title="Itaobuy Spreadsheet Borse"
              className="w-full rounded-2xl"
            />
          </div>

          <section className="mt-12">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-warm-950">
              Guida alla scelta della <span className="text-tealwarm-600">borsa perfetta</span>
            </h2>
            <p className="mt-4 text-sm md:text-base text-warm-700 leading-relaxed">
              Scegliere una borsa senza vederla di persona può essere complicato, ma con l&apos;<strong>itaobuy sheet</strong> hai tutti gli strumenti per decidere con sicurezza. Ecco i tre aspetti fondamentali da controllare nelle foto QC prima di scegliere:
            </p>
            <div className="mt-5 space-y-4">
              {[
                { title: 'Materiali e rifiniture', desc: 'Controlla la grana della pelle, la trama del tessuto e la qualità delle rifiniture sui bordi. Una borsa ben fatta ha bordi puliti, senza sbavature di colla o fili sporgenti.' },
                { title: 'Cerniere e hardware', desc: 'Zip, ganci e bottoni devono essere robusti e ben fissati. Nelle foto QC verifica che le zip scorrano su binari dritti e che i ganci siano saldamente ancorati al tessuto.' },
                { title: 'Interno e scomparti', desc: 'Le migliori borse hanno interni ben organizzati con tasche, divisori e fodere di qualità. Le foto QC ti mostrano esattamente com\'è fatto l\'interno, così sai cosa aspettarti.' },
              ].map((item, i) => (
                <div key={i} className="bg-cream-50 rounded-2xl p-5 flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-tealwarm-100 flex items-center justify-center text-tealwarm-700 flex-shrink-0 mt-0.5">
                    <span className="text-sm font-bold">{i + 1}</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-warm-900 text-base">{item.title}</h3>
                    <p className="mt-1 text-sm text-warm-600 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-12">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-warm-950">
              Tipi di <span className="text-tealwarm-600">borse</span> più popolari
            </h2>
            <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="bg-tealwarm-50 rounded-2xl p-5 text-center">
                <div className="w-12 h-12 rounded-full bg-tealwarm-100 flex items-center justify-center text-tealwarm-700 mx-auto mb-3">
                  <i className="ri-briefcase-line text-xl"></i>
                </div>
                <h3 className="font-bold text-warm-900">Zaini</h3>
                <p className="mt-2 text-sm text-warm-600">Urbani, sportivi e da viaggio — controllo spallacci imbottiti e scomparti laptop.</p>
              </div>
              <div className="bg-cream-100 rounded-2xl p-5 text-center">
                <div className="w-12 h-12 rounded-full bg-cream-200 flex items-center justify-center text-warm-700 mx-auto mb-3">
                  <i className="ri-handbag-line text-xl"></i>
                </div>
                <h3 className="font-bold text-warm-900">Tracolla</h3>
                <p className="mt-2 text-sm text-warm-600">Compatte e versatili — verifica lunghezza tracolla regolabile e chiusure sicure.</p>
              </div>
              <div className="bg-warm-50 rounded-2xl p-5 text-center">
                <div className="w-12 h-12 rounded-full bg-warm-100 flex items-center justify-center text-warm-600 mx-auto mb-3">
                  <i className="ri-shopping-bag-line text-xl"></i>
                </div>
                <h3 className="font-bold text-warm-900">Tote Bag</h3>
                <p className="mt-2 text-sm text-warm-600">Spaziose e pratiche — controlla resistenza manici e cuciture sul fondo.</p>
              </div>
            </div>
          </section>

          <section className="mt-12">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-warm-950">Domande frequenti</h2>
            <div className="mt-5 space-y-4">
              {[
                { q: 'Che tipo di borse posso trovare su Itaobuy Spreadsheet?', a: 'Zaini urbani, borse a tracolla in pelle, tote bag in tela, borse a mano eleganti e marsupi. Ogni borsa include foto QC dettagliate.' },
                { q: 'Come valutare una borsa dalle foto QC?', a: 'Controlla la qualità della pelle/tessuto, le cuciture su manici e fondo, le cerniere e le finiture metalliche. Le foto mostrano anche l\'interno.' },
                { q: 'Itaobuy Spreadsheet ha borse di marche conosciute?', a: 'Lo spreadsheet include prodotti di vari produttori. Le foto QC ti aiutano a confrontare qualità e design indipendentemente dal brand.' },
              ].map((item, i) => (
                <div key={i} className="bg-cream-50 rounded-2xl p-5">
                  <h3 className="font-bold text-warm-900">{item.q}</h3>
                  <p className="mt-2 text-sm text-warm-600 leading-relaxed">{item.a}</p>
                </div>
              ))}
            </div>
          </section>

          <div className="mt-12 bg-gradient-to-r from-tealwarm-50 to-cream-50 rounded-2xl p-8 text-center">
            <h2 className="font-display text-2xl font-bold text-warm-950">
              Esplora le <span className="text-tealwarm-600">borse</span> su Itaobuy Sheet
            </h2>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="https://kaspreadsheet.com/search-921/name/Shoes.html" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-warm-900 text-white text-sm font-semibold hover:bg-warm-800 transition-all whitespace-nowrap cursor-pointer">
                Borse su Itaobuy Sheet <i className="ri-arrow-right-up-line"></i>
              </a>
              <Link to="/" className="text-sm text-tealwarm-700 hover:text-tealwarm-800 font-medium transition-colors">
                <i className="ri-arrow-left-line mr-1"></i> Torna alla homepage
              </Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}